export const slugify = (text = "") =>
  text
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const formatBlogDate = (value = new Date()) =>
  new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export const FONT_FAMILIES = [
  { label: "Poppins", value: "Poppins, sans-serif" },
  { label: "Roboto", value: "Roboto, sans-serif" },
  { label: "Mulish", value: "Mulish, sans-serif" },
  { label: "Playfair Display", value: '"Playfair Display", serif' },
  { label: "Times New Roman", value: '"Times New Roman", Times, serif' },
  { label: "Georgia", value: "Georgia, serif" },
];

export const FONT_SIZES = [
  "14px",
  "16px",
  "17px",
  "18px",
  "20px",
  "24px",
  "28px",
  "30px",
  "32px",
  "36px",
  "42px",
];

export const FONT_STYLES = [
  { label: "Normal", value: "normal" },
  { label: "Italic", value: "italic" },
  { label: "Bold", value: "bold" },
  { label: "Bold italic", value: "bold-italic" },
];

export const DEFAULT_SECTION_STYLES = {
  headingFontFamily: "Poppins, sans-serif",
  headingFontSize: "30px",
  headingFontStyle: "bold",
  contentFontFamily: "Roboto, sans-serif",
  contentFontSize: "17px",
  contentFontStyle: "normal",
};

export const getFontCss = (fontStyle = "normal") => {
  if (fontStyle === "italic") return { fontStyle: "italic", fontWeight: 500 };
  if (fontStyle === "bold") return { fontStyle: "normal", fontWeight: 800 };
  if (fontStyle === "bold-italic") return { fontStyle: "italic", fontWeight: 800 };
  return { fontStyle: "normal", fontWeight: 400 };
};

export const parseTags = (value) => {
  if (Array.isArray(value)) {
    return value.map((tag) => String(tag).trim()).filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
};

export const getSecondaryKeywords = (blog = {}) =>
  parseTags(
    blog.secondaryKeywords?.length ? blog.secondaryKeywords : blog.secondaryKeyword
  );

export const formatReadTime = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const match = String(value).match(/(\d+)/);
  return match ? `${Math.max(1, Number(match[1]))} min read` : String(value).trim();
};

export const readTimeMinutes = (value) => {
  const match = String(value || "").match(/(\d+)/);
  return match ? match[1] : "";
};

export const estimateReadTime = (content = []) => {
  const words = content.reduce((count, block) => {
    if (block.type === "image" || block.type === "video" || block.type === "embed") {
      return count;
    }
    const text = [block.heading, ...(block.paragraphs || []), block.body || ""].join(" ");
    return count + text.trim().split(/\s+/).filter(Boolean).length;
  }, 0);

  return `${Math.max(1, Math.round(words / 200) || 1)} min read`;
};

export const parseMediaUrl = (raw = "") => {
  const value = String(raw || "").trim();
  if (!value) return null;

  const youtube = value.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([A-Za-z0-9_-]{11})/
  );
  if (youtube) {
    return {
      kind: "youtube",
      href: value,
      embedSrc: `https://www.youtube.com/embed/${youtube[1]}`,
    };
  }

  const vimeo = value.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) {
    return {
      kind: "vimeo",
      href: value,
      embedSrc: `https://player.vimeo.com/video/${vimeo[1]}`,
    };
  }

  try {
    const parsed = new URL(value.startsWith("http") ? value : `https://${value}`);
    return { kind: "link", href: parsed.href, embedSrc: "" };
  } catch {
    return null;
  }
};

export const fileToDataUrl = (file, maxBytes = 8 * 1024 * 1024) =>
  new Promise((resolve, reject) => {
    if (file.size > maxBytes) {
      reject(new Error(`Please choose a file smaller than ${Math.round(maxBytes / 1024 / 1024)}MB.`));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not upload that file."));
    reader.readAsDataURL(file);
  });

export const paragraphsFromText = (text = "") =>
  text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.replace(/\n/g, " ").trim())
    .filter(Boolean);

export const textFromParagraphs = (paragraphs = []) => paragraphs.join("\n\n");

export const fileToCompressedDataUrl = (file, maxWidth = 1600, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const image = new Image();

      image.onload = () => {
        const scale = Math.min(1, maxWidth / image.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));

        const context = canvas.getContext("2d");
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };

      image.onerror = () => reject(new Error("Could not read that image."));
      image.src = reader.result;
    };

    reader.onerror = () => reject(new Error("Could not upload that file."));
    reader.readAsDataURL(file);
  });
